// Helpers for the group-head "compare IECs" view: line up several IECs' summary rows
// and work out the Combined figures.
//
// Combined percentages are never averaged or summed from row counts. Each percentage
// column is a share of something — either of a column total (e.g. Closed SHB count /
// Total Entitlement SHB count) or of another column on the same row (e.g. FTA BCD
// availed / total eligible). We detect which, per IEC, and pool the underlying numbers.

// Keys that always hold a row's label, even when the label looks numeric.
const LABEL_KEYS = [
  'description', 'remark', 'final_remark', 'business_unit', 'particulars',
  'category', 'status', 'type', 'name', 'remarks', 'scheme', 'lic_no',
  'license_no', 'bond_no', 'hsn_consistency_remark',
];

// Count-like columns are tried first when looking for what a percentage is a share of.
const COUNT_KEY_RE = /^(summary|count)$|count|no_of|_cnt$/i;

export const isPctKey = (key) => /percent/i.test(key);

export const toNum = (v) => {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean' || typeof v === 'object') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const round2 = (n) => Math.round(n * 100) / 100;

export const rowLabel = (row) => {
  if (!row) return '';
  const key = LABEL_KEYS.find(k => row[k] !== undefined && row[k] !== null && row[k] !== '');
  return key ? String(row[key]).trim() : '';
};

// Pick the { headers, dashboard_summary } block, handling multi-mode cards (All / Sea / Air / Land).
export const MODES = ['All', 'Sea', 'Air', 'Land'];
export const resolveSummaryBlock = (raw, mode) => {
  if (!raw) return null;
  if (raw.dashboard_summary) return raw;
  const modes = MODES.filter(m => raw[m]);
  if (!modes.length) return null;
  return raw[modes.includes(mode) ? mode : modes[0]] || null;
};

export const availableModes = (raws) => {
  const found = new Set();
  raws.forEach(raw => {
    if (raw && !raw.dashboard_summary) MODES.forEach(m => raw[m] && found.add(m));
  });
  return MODES.filter(m => found.has(m));
};

// Flatten grouped (children) headers into their leaf columns.
export const leafHeaders = (headers = []) => headers.flatMap(h => (
  Array.isArray(h.children) && h.children.length ? leafHeaders(h.children) : [h]
));

const isGrandTotal = (row) => row?.key === 'grand_total' || /^grand\s*total$/i.test(rowLabel(row));

// Row labels across every IEC. A label only some IECs have is slotted in after the row it
// follows in that IEC's own list (so it never lands below a trailing total), and Grand Total
// always comes first.
export const unionRowLabels = (rowSets) => {
  const order = [];
  const grandTotals = new Set();
  rowSets.forEach(rows => {
    const own = new Set((rows || []).map(rowLabel));
    let prev = null;
    (rows || []).forEach(r => {
      const label = rowLabel(r);
      if (!label) return;
      if (isGrandTotal(r)) grandTotals.add(label);
      if (!order.includes(label)) {
        let at = prev === null ? 0 : order.indexOf(prev) + 1;
        // Step past rows earlier IECs added here, so IEC-only rows keep the IEC order.
        while (at < order.length && !own.has(order[at])) at += 1;
        order.splice(at, 0, label);
      }
      prev = label;
    });
  });
  return [...order.filter(l => grandTotals.has(l)), ...order.filter(l => !grandTotals.has(l))];
};

// Percentages arrive rounded to 2 decimals (a few reports round to whole numbers), so a
// recomputed share only has to land within that rounding.
const pctFits = (expected, reported, wholeNumbers) => (
  Math.abs(expected - reported) <= (wholeNumbers ? 0.51 : 0.011) + reported * 0.002
);
const isWholeNumberPct = (rows, pctKey) => rows.every(r => {
  const p = toNum(r[pctKey]);
  return p === null || Number.isInteger(p);
});

// "Share of a column total": every row's value * 100 / pct points at the same base
// (the 100% figure). Returns { key, base } or null.
const findShareBasis = (rows, pctKey, numericKeys) => {
  const whole = isWholeNumberPct(rows, pctKey);
  for (const key of numericKeys) {
    const points = rows
      .map(r => ({ p: toNum(r[pctKey]), v: toNum(r[key]) }))
      .filter(({ p, v }) => p > 0 && v > 0);
    if (!points.length) continue;
    // Percentages arrive rounded, so small shares imply a fuzzy base. Take the base from
    // the largest share (the Total row when there is one), and only check the rest fit.
    const anchor = points.reduce((best, pt) => (pt.p > best.p ? pt : best));
    const base = anchor.p >= 99.995 ? anchor.v : (anchor.v * 100) / anchor.p;
    if (points.every(({ p, v }) => pctFits((v * 100) / base, p, whole))) return { key, base };
  }
  return null;
};

// "Ratio of two columns on the same row": pct = a / b * 100 for every row. Returns [a, b] or null.
const findRowRatio = (rows, pctKey, numericKeys) => {
  const usable = rows.filter(r => toNum(r[pctKey]) !== null);
  if (!usable.length) return null;
  const whole = isWholeNumberPct(usable, pctKey);
  for (const a of numericKeys) {
    for (const b of numericKeys) {
      if (a === b) continue;
      const fits = usable.every(r => {
        const p = toNum(r[pctKey]);
        const num = toNum(r[a]) || 0;
        const den = toNum(r[b]) || 0;
        if (den === 0) return p === 0 || p === null;
        // Reversals can make a row's figures negative (e.g. -₹28 L eligible), and the backend
        // isn't consistent about the % it sends for those (100% on the BU row, 0% on its Grand
        // Total), so such rows neither confirm nor rule out a formula.
        if (den < 0) return true;
        return pctFits((num / den) * 100, p, whole);
      });
      if (fits && usable.some(r => (toNum(r[b]) || 0) > 0)) return [a, b];
    }
  }
  return null;
};

const hasAnyValue = (rows, key) => rows.some(r => (toNum(r[key]) || 0) !== 0);

// True when an IEC's summary has at least one non-zero figure (not just empty buckets).
export const hasSummaryData = (rows) => (Array.isArray(rows) ? rows : []).some(r => (
  Object.entries(r).some(([k, v]) => !LABEL_KEYS.includes(k) && (toNum(v) || 0) !== 0)
));

/**
 * Combine several IECs' summary rows.
 * @param {Array<Array<object>>} rowSets one array of summary rows per IEC
 * @returns {{ labels: string[], combined: object[], byLabel: Array<Map<string, object>> }}
 *   combined[i] is the pooled row for labels[i]; byLabel[k] maps label -> row for IEC k.
 */
export const combineSummaryRows = (rowSets) => {
  const sets = rowSets.map(rows => (Array.isArray(rows) ? rows : []));
  const allRows = sets.flat();
  const labels = unionRowLabels(sets);

  const keys = [];
  allRows.forEach(r => Object.keys(r).forEach(k => { if (!keys.includes(k)) keys.push(k); }));

  const numericKeys = keys
    .filter(k => !LABEL_KEYS.includes(k) && !isPctKey(k))
    .filter(k => {
      const values = allRows.map(r => r[k]).filter(v => v !== null && v !== undefined && v !== '');
      return values.length > 0 && values.every(v => toNum(v) !== null);
    })
    .sort((a, b) => Number(!COUNT_KEY_RE.test(a)) - Number(!COUNT_KEY_RE.test(b)));
  const pctKeys = keys.filter(isPctKey);

  // Decide, for each percentage column, how it pools across IECs. A same-row ratio is
  // checked first: it has to hold on every row of every IEC, so a match is unambiguous,
  // whereas a single-row report fits "share of a column total" by coincidence.
  const pctPlans = {};
  pctKeys.forEach(pk => {
    const ratio = findRowRatio(allRows, pk, numericKeys);
    if (ratio) {
      pctPlans[pk] = { mode: 'ratio', ratio };
      return;
    }
    const withData = sets.filter(rows => hasAnyValue(rows, pk));
    const shares = sets.map(rows => findShareBasis(rows, pk, numericKeys));
    pctPlans[pk] = withData.length && sets.every((rows, i) => shares[i] || !hasAnyValue(rows, pk))
      ? { mode: 'share', shares }
      : { mode: 'none' };
  });

  const byLabel = sets.map(rows => {
    const map = new Map();
    rows.forEach(r => {
      const label = rowLabel(r);
      if (label && !map.has(label)) map.set(label, r);
    });
    return map;
  });

  const combined = labels.map(label => {
    const matches = byLabel.map(map => map.get(label) || null);
    const first = matches.find(Boolean) || {};
    const out = {};

    keys.forEach(k => {
      if (!numericKeys.includes(k) && !pctKeys.includes(k)) out[k] = first[k];
    });

    numericKeys.forEach(k => {
      let any = false;
      let sum = 0;
      matches.forEach(m => {
        const v = m ? toNum(m[k]) : null;
        if (v !== null) {
          any = true;
          sum += v;
        }
      });
      out[k] = any ? round2(sum) : null;
    });

    pctKeys.forEach(pk => {
      const plan = pctPlans[pk];
      let num = 0;
      let den = 0;
      if (plan.mode === 'share') {
        plan.shares.forEach((share, i) => {
          if (!share) return;
          num += toNum(matches[i]?.[share.key]) || 0;
          den += share.base;
        });
      } else if (plan.mode === 'ratio') {
        const [a, b] = plan.ratio;
        matches.forEach(m => {
          if (!m) return;
          num += toNum(m[a]) || 0;
          den += toNum(m[b]) || 0;
        });
      }
      const anyPct = matches.some(m => m && toNum(m[pk]) !== null);
      out[pk] = plan.mode !== 'none' && anyPct ? (den !== 0 ? round2((num / den) * 100) : 0) : null;
    });

    return out;
  });

  return { labels, combined, byLabel };
};

// Short display name for an IEC, e.g. "CUMMINS INDIA LIMITED- CIL" -> "CIL".
export const shortIecName = (fullname, iecNo) => {
  const name = String(fullname || '').trim();
  if (!name) return iecNo;
  const tail = name.split('-').pop().replace(/[()]/g, '').trim();
  return tail && tail.length <= 12 ? tail : name;
};
